import { t } from '@lingui/core/macro';
import { useRef } from 'react';

import PlusIcon from '@/assets/svg/bx-plus.svg?react';
import { Button } from '@/components/buttons';
import { ButtonGroup } from '@/components/buttons/ButtonGroup';
import { Field, FieldError } from '@/components/Form/fields';
import { Text } from '@/components/typography';

import ProductAreaBadge from './ProductAreaBadge';
import { submitOnEnter } from './submitOnEnter';
import { MAX_PRODUCT_AREAS, useAIVisibilitySettingsForm } from './useAIVisibilitySettingsForm';

const BrandProductAreas = () => {
  const form = useAIVisibilitySettingsForm();
  const previousBrandProductAreas = useRef([...form.getFieldValue('brandProductAreas')]);

  return (
    <form.AppField
      name="brandProductAreas"
      mode="array"
      listeners={{
        onChange: ({ value, fieldApi }) => {
          fieldApi.form.setFieldValue('showCompetitors', true);
          const addedAreas = value.filter(
            (area) => !previousBrandProductAreas.current.includes(area),
          );
          previousBrandProductAreas.current = [...value];
          fieldApi.form.getFieldValue('competitors').forEach((competitor, index) => {
            const previousAreas = competitor.comparedProductAreas;
            fieldApi.form.setFieldValue(`competitors[${index}].comparedProductAreas`, [
              ...previousAreas.filter((area) => value.includes(area)),
              ...addedAreas,
            ]);
          });
        },
      }}
    >
      {(productAreasField) => {
        const productAreas = productAreasField.state.value;
        const canAdd = productAreas.length < MAX_PRODUCT_AREAS;
        const isEmpty = productAreas.length === 0;
        const errors = productAreasField.state.meta.errors;
        const isInvalid = errors.length > 0;

        return (
          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => (
              <Field aria-label={t`Product areas`} data-invalid={isInvalid ? true : undefined}>
                <div className="flex flex-wrap items-center gap-2">
                  {productAreas.map((name, index) => (
                    <ProductAreaBadge
                      key={name}
                      name={name}
                      disabled={isSubmitting}
                      onClose={() => productAreasField.removeValue(index)}
                    />
                  ))}
                  {canAdd && (
                    <form.AppField name="newProductArea">
                      {(newProductAreaField) => {
                        const handleAdd = () => {
                          const name = newProductAreaField.state.value.trim();
                          if (!name || productAreas.includes(name) || !canAdd) {
                            return;
                          }

                          productAreasField.pushValue(name);
                          newProductAreaField.handleChange('');
                        };

                        return (
                          <ButtonGroup shape="round" className="w-56">
                            <newProductAreaField.TextInput
                              id="ai-visibility-new-product-area"
                              aria-label={t`New product area`}
                              placeholder={t`Add product area...`}
                              size="medium"
                              onKeyDown={submitOnEnter(handleAdd)}
                            />
                            <Button
                              className="px-2"
                              aria-label={t`Add`}
                              disabled={isSubmitting}
                              onClick={handleAdd}
                            >
                              <PlusIcon />
                            </Button>
                          </ButtonGroup>
                        );
                      }}
                    </form.AppField>
                  )}
                </div>
                {!isEmpty && (
                  <Text
                    variant="caption1"
                    color="grey"
                    className="block"
                    aria-label={t`${productAreas.length} of ${MAX_PRODUCT_AREAS} product areas`}
                  >
                    {productAreas.length} / {MAX_PRODUCT_AREAS}
                  </Text>
                )}
                {isInvalid && <FieldError errors={errors} />}
              </Field>
            )}
          </form.Subscribe>
        );
      }}
    </form.AppField>
  );
};

export default BrandProductAreas;
